import { type ReactNode } from 'react';
import { useStudio } from './RepositoryContext';
import { useAsyncData } from './useAsyncData';
import styles from './RailStatus.module.css';

/**
 * The readout at the foot of the nav rail.
 *
 * The rail is sized by the viewport, not by its eight sections, so on any
 * desktop screen it ran out of nav well before it ran out of height. This
 * fills that with the one number a grower opens the app to check: how much
 * is alive right now. It is the only place the sprout's lime appears in the
 * chrome, which is the whole reason to spend a colour on it -- lime means
 * living, and nothing else in the interface does.
 *
 * It renders nothing until the counts have settled. A skeleton here would
 * flicker in the corner of every navigation for no information gained.
 */
export function RailStatus(): ReactNode {
  const { repositories, ready } = useStudio();
  const data = useAsyncData(
    async () => ({
      spaces: await repositories.spaces.listSpaces(),
      cycles: await repositories.grows.listCycles(),
    }),
    'grow-summary',
  );

  if (!ready || !data.data) {
    return null;
  }

  const active = data.data.cycles.filter(
    (cycle) => cycle.status === 'active' || cycle.status === 'drying',
  ).length;
  const spaces = data.data.spaces.length;

  return (
    <div className={styles.readout}>
      <p className={styles.caption}>
        <span
          className={active > 0 ? styles.pipLive : styles.pipIdle}
          aria-hidden="true"
        />
        {active > 0 ? 'Running now' : 'Nothing running'}
      </p>
      <p className={styles.counts}>
        <span className={styles.figure}>{active}</span>{' '}
        {active === 1 ? 'grow' : 'grows'}
        <span className={styles.divider}>·</span>
        <span className={styles.figure}>{spaces}</span>{' '}
        {spaces === 1 ? 'space' : 'spaces'}
      </p>
    </div>
  );
}
